export const DRAFT_KIND_VERSION = 'version';
export const DRAFT_KIND_HISTORY = 'history';
export const BACKUP_NAME_PREFIX = 'BACKUP-';
export const HISTORY_NAME_PREFIX = '__history__-';
export const INTERNAL_VERSION_PREFIX = '__v__-';

const VERSION_SUFFIX_RE = /\s+Version(?:\s+\d+)?$/i;

export function isBackupDraftName(name) {
    return typeof name === 'string' && name.startsWith(BACKUP_NAME_PREFIX);
}

export function isHistoryDraftName(name) {
    return typeof name === 'string' && name.startsWith(HISTORY_NAME_PREFIX);
}

export function isHistoryDraft(draft) {
    if (!draft) return false;
    if (draft.kind === DRAFT_KIND_HISTORY) return true;
    return isHistoryDraftName(draft.draft_name);
}

export function isHiddenFromVersions(draft) {
    if (isHistoryDraft(draft)) return true;
    return isBackupDraftName(draft?.draft_name);
}

export function cleanInheritedTitle(name) {
    if (typeof name !== 'string') return '';
    const trimmed = name.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith(HISTORY_NAME_PREFIX) || trimmed.startsWith(INTERNAL_VERSION_PREFIX)) {
        return '';
    }
    const stripped = trimmed.replace(VERSION_SUFFIX_RE, '').trim();
    return stripped || trimmed;
}

export function getDraftTitle(draft) {
    if (!draft) return '';
    const display = typeof draft.display_name === 'string' ? draft.display_name.trim() : '';
    if (display) return display;
    return cleanInheritedTitle(draft.draft_name || draft.name) || 'Schedule';
}

export function formatVersionLabel(versionNumber) {
    if (versionNumber == null || versionNumber === '') return '';
    const numeric = Number(versionNumber);
    if (!Number.isFinite(numeric)) return '';
    return `v${numeric}`;
}

export function formatEditingDraftLabel(draft) {
    const title = getDraftTitle(draft);
    const version = formatVersionLabel(draft?.version_number);
    if (!title) return version;
    return version ? `${title} (${version})` : title;
}

export function formatDraftOptionLabel(draft) {
    const title = getDraftTitle(draft);
    const version = formatVersionLabel(draft?.version_number);
    if (!title) return version;
    return version ? `${title} · ${version}` : title;
}

export function generateVersionName(period, drafts = []) {
    const existingNames = new Set((drafts || []).map((draft) => draft?.draft_name).filter(Boolean));
    const periodKey = period?.id != null ? String(period.id) : 'none';
    let name = '';
    do {
        const suffix = Math.random().toString(36).slice(2, 8);
        name = `${INTERNAL_VERSION_PREFIX}${periodKey}-${Date.now().toString(36)}-${suffix}`;
    } while (existingNames.has(name));
    return name;
}

export function findReusableWorkingDraft({ drafts = [], periodId, liveDraftId } = {}) {
    if (liveDraftId == null || liveDraftId === '') {
        return null;
    }

    const workingDrafts = (drafts || []).filter((draft) => {
        if (isHiddenFromVersions(draft)) return false;
        if (periodId != null && String(draft?.period_id ?? draft?.periodId ?? '') !== String(periodId)) {
            return false;
        }
        if (String(draft?.id) === String(liveDraftId)) return false;
        const sourceId = draft?.source_draft_id ?? draft?.sourceDraftId;
        return sourceId != null && String(sourceId) === String(liveDraftId);
    });

    if (workingDrafts.length === 0) return null;

    return [...workingDrafts].sort((a, b) => {
        const aTime = new Date(a.last_modified || a.updated_at || a.created_at || 0).getTime();
        const bTime = new Date(b.last_modified || b.updated_at || b.created_at || 0).getTime();
        return bTime - aTime;
    })[0];
}

export function formatHistoryTimestamp(value) {
    if (!value) return 'Unknown time';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown time';
    return date.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
    });
}

export function formatDraftDisplayName(draft, { historyList = false } = {}) {
    if (!draft) return '';
    if (historyList && isHistoryDraft(draft)) {
        return formatHistoryTimestamp(draft.created_at || draft.last_modified);
    }
    return getDraftTitle(draft);
}

export function isDraftLockError(error) {
    const code = error?.code || error?.response?.data?.code;
    return code === 'DRAFT_LOCKED_LIVE' || code === 'DRAFT_LOCKED_HISTORY';
}
